import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ETaxDocument } from "../../../types/e-tax.type";
import { EtaxAutoSendRoundStatus } from "../../../types/etax-auto-send.type";
import { formatThaiShortDateTimeBkk } from "../../../utils/date";
import { EtaxStatusBadge } from "../documentCells";
import {
  EXPIRED_LABEL,
  EXPIRED_TOOLTIP,
  HELD_LABEL,
  getAutoRetryLabel,
  getAutoSendRowState,
  getNeedsAckReason,
  getRoundStatusMeta,
  getScheduledLabel,
  getSentByLabel,
  isAutoRetryPending,
  isPaperDocument,
} from "./autoSendMeta";

export const tintedChipSx = (color: string) => ({
  color,
  bgcolor: `${color}1A`,
  fontWeight: 500,
  borderRadius: "16px",
  height: 24,
});

export const RoundStatusChip = ({
  status,
}: {
  status: EtaxAutoSendRoundStatus | null | undefined;
}) => {
  const { label, color } = getRoundStatusMeta(status);
  return <Chip label={label} size="small" sx={tintedChipSx(color)} />;
};

const TooltipLines = ({ lines }: { lines: string[] }) => (
  <Box>
    {lines.map((line) => (
      <Box key={line}>{line}</Box>
    ))}
  </Box>
);

/**
 * The document's send status plus where it stands with the weekly round.
 *
 * The send status chip is left exactly as it was — ค้างส่ง, ส่งไม่ได้ and the KTB lifecycle
 * labels keep their meaning — and the auto-send facts sit beside and under it: a hold or the
 * round that will pick the bill up (unsent bills), who sent it (sent bills), and a warning
 * marker while a failure waits for someone to acknowledge it. A bill KTB could not be reached
 * for carries how many hourly re-sends it has used; once it runs out, the acknowledgement
 * marker takes over. A bill whose tax month KTB has already closed shows เลยกำหนดส่ง in place
 * of the send status and of every hold / round / re-send chip — none of them can send it now.
 *
 * `retryMax` is the settings' `transient_retry_max`; without it the chip shows the count alone.
 */
export const EtaxDocumentStatusCell = ({
  doc,
  retryMax,
}: {
  doc: ETaxDocument;
  retryMax?: number | null;
}) => {
  const state = getAutoSendRowState(doc);
  const expired = state === "EXPIRED";
  const sentBy = getSentByLabel(doc);

  const holdLines = [
    `โดย ${doc.held_by ?? "-"}`,
    `เมื่อ ${formatThaiShortDateTimeBkk(doc.held_at) || "-"}`,
    ...(doc.hold_note ? [`หมายเหตุ: ${doc.hold_note}`] : []),
  ];

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
      <Box display="flex" alignItems="center" gap={0.5}>
        {expired ? (
          <Tooltip arrow title={EXPIRED_TOOLTIP}>
            <Chip
              label={EXPIRED_LABEL}
              size="small"
              sx={{ ...tintedChipSx("#6a6a6a"), minWidth: 84 }}
            />
          </Tooltip>
        ) : (
          <EtaxStatusBadge
            status={doc.etax_status}
            eligible={doc.etax_eligible}
            documentStatus={doc.document_status}
            reasons={doc.etax_reason_messages}
            isPaper={isPaperDocument(doc)}
          />
        )}
        {doc.needs_ack && (
          <Tooltip arrow title={`รอรับทราบ: ${getNeedsAckReason(doc)}`}>
            <WarningAmberIcon
              fontSize="small"
              color="warning"
              aria-label="รอรับทราบ"
            />
          </Tooltip>
        )}
      </Box>

      {state === "HELD" && (
        <Tooltip arrow title={<TooltipLines lines={holdLines} />}>
          <Chip label={HELD_LABEL} size="small" sx={tintedChipSx("#6a6a6a")} />
        </Tooltip>
      )}

      {!expired && isAutoRetryPending(doc) && (
        <Tooltip arrow title="ส่งถึง KTB ไม่สำเร็จ ระบบจะลองส่งใหม่อัตโนมัติทุกชั่วโมง">
          <Chip
            label={getAutoRetryLabel(doc.transient_retry_count, retryMax)}
            size="small"
            sx={tintedChipSx("#FDA42C")}
          />
        </Tooltip>
      )}

      {state === "SCHEDULED" && (
        <Chip
          label={getScheduledLabel(doc.next_round_at)}
          size="small"
          sx={tintedChipSx("#5C69E8")}
        />
      )}

      {sentBy && (
        <Typography variant="caption" color="text.secondary" noWrap>
          {sentBy}
        </Typography>
      )}
    </Box>
  );
};
